import {
  EMPTY,
  EmptyError,
  Observable,
  pipe,
  throwError,
  UnaryFunction,
} from 'rxjs';
import { catchError, first, repeat, tap } from 'rxjs/operators';
import {
  jwtRefreshedSignal,
  signalJwtAuthError,
} from '../models/data-access-environment';

/**
 * Use this in conjuntion with JwtAuthHeaders to refresh the JWT when there is
 * an authorization error. If the JWT cannot be refreshed, a 401 Unauthorized
 * response will be thrown.
 *
 * Do NOT use this with FleetJwtAuthHeaders because that JWT is (currently) not
 * refreshable.
 *
 */
export function retryWhenJwtAuthError(): UnaryFunction<
  Observable<Response>,
  Observable<Response>
> {
  let hasAuthError = false;

  function checkForAuthError(response: Response) {
    const status = response.status;
    return status === 401 || status === 403;
  }

  function waitForJwtRefreshedSignal() {
    if (hasAuthError) {
      signalJwtAuthError();
      return jwtRefreshedSignal.asObservable();
    }

    return EMPTY;
  }

  return pipe(
    tap((response) => {
      hasAuthError = checkForAuthError(response);
    }),
    repeat({
      count: 2,
      delay: waitForJwtRefreshedSignal,
    }),
    first((response) => !checkForAuthError(response)),
    catchError((error) =>
      throwError(() =>
        error instanceof EmptyError
          ? new Response(null, { status: 401, statusText: 'Unauthorized' })
          : error
      )
    )
  );
}
